import { useState, useEffect } from 'react';
import { Tooltipped, FontIcon, HelpOutlineSVGIcon } from 'react-md';
import { useLocation } from 'react-router-dom';
import TextTruncate from 'react-text-truncate';
import ThItem from './ThItem';
import { resolvePropDepth } from '../../utils';
import _ from 'lodash';
import cx from 'classnames';
import styles from './EventVIPSTable.module.scss';

const EventVIPSTable = ({ labelKeys = [], addItem, removeItem, data = [], duplicates, selected }) => {
  const [activeSortKey, setActiveSortKey] = useState('');
  const [sortedData, setSortedData] = useState([]);
  const [sortDirection, setSortDirection] = useState('asc');
  const { pathname = '' } = useLocation();

  useEffect(() => {
    const formatString = str => str.trim().toLowerCase();
    const initialNameSort = [...data].sort((a, b) => {
      const name1 = a.lastName || '';
      const name2 = b.lastName || '';

      return formatString(name1) < formatString(name2) ? -1 : 1;
    });

    return pathname === '/vips' ? setSortedData(initialNameSort) : setSortedData(data);
  }, [data, pathname]);

  useEffect(() => {
    if (activeSortKey !== '') {
      setSortedData(
        _.orderBy(data, [x => x[activeSortKey] && x[activeSortKey].toString().toLowerCase()], sortDirection)
      );
    }
  }, [activeSortKey, data, sortDirection]);

  const handleClick = (item) => {
    if (!selected.includes(item.id)) {
      addItem(item.id);

    } else {
      removeItem(item.id);
    }
  };

  const sortData = (key = 'fullName', sortable) => {
    if (sortable) {
      if (key === activeSortKey) {
        return setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      }

      setActiveSortKey(key);
      setSortDirection('asc');
    }
  };

  const trClass = (id) => {
    const dups = duplicates || [];
    const isDuplicate = dups.includes(id);
    const isSelected = selected.includes(id);

    const status = cx({
      [styles.duplicate]: isDuplicate,
      [styles.selected]: isSelected,
      [styles.duplicateSelected]: isDuplicate && isSelected
    });

    return status;
  };

  const format = (_data, formatter) => (!!formatter && !!_data) ? formatter(_data) : !!_data ? _data : '';

  return (
    <>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th />
            {labelKeys.map(lk => {
              const { key = '', label = '', sortable = true } = lk;
              const sortKey = key === 'fullName' ? 'lastName' : key;
              const thClassName = cx([
                styles[`columns-${labelKeys.length}`],
                { [styles.sortable]: sortable }
              ]);

              return (
                <th className={thClassName} key={`th-${key}`}>
                  <ThItem
                    className={styles.thItem}
                    iconClassName={styles.thIcon}
                    activeSortKey={activeSortKey}
                    sortKey={sortable && sortKey}
                    sortable={sortable}
                    onClick={sortData}
                    direction={sortDirection}
                  >
                    {label}
                  </ThItem>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map(item => {
            const { id = '' } = item;

            return (
              <tr key={`tr-${id}`} className={trClass(id)} onClick={() => handleClick(item)}>
                <td>
                  <FontIcon className={styles.checkbox}>
                    {selected.includes(id) ? 'check_box' : 'check_box_outline_blank'}
                  </FontIcon>
                </td>
                {labelKeys.map(lk => {
                  const dataKey = `${id}-${lk.key}`;

                  if (lk.key === 'lots') {
                    const lotKey = item[lk.key];
                    const number = lotKey.length;
                    const lotsLabel = [...lotKey]
                      .sort((a, b) => (a.name < b.name ? -1 : 1)) // alphabetical sort
                      .map(lot => <li key={lot.id}>{lot.name}</li>);

                    return (
                      <td key={dataKey}>
                        <div className={styles[lk.key]}>
                          <span>
                            {number} lot{number !== 1 ? 's' : ''}
                          </span>
                          <span className={styles.tooltipContainer}>
                            {lk.key === 'lots' && (
                              <Tooltipped
                                dense
                                id="lot-permissions-tooltip"
                                tooltip={number > 0 ? <ul className={styles.tooltipList}>{lotsLabel}</ul> : null}
                                className="tooltip"
                                position="right"
                              >
                                <div style={{ position: 'relative' }}>
                                  <HelpOutlineSVGIcon className={styles.helpIcon} />
                                </div>
                              </Tooltipped>
                            )}
                          </span>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td key={dataKey} className={`td-${lk.key.toLowerCase()}`}>
                      <TextTruncate text={format(resolvePropDepth(lk.key, item)?.toString(), lk?.formatter)} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default EventVIPSTable;
